import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, ArrowRight, Calendar, Clock, Headphones, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlogContent } from "@/components/blog/BlogContent";
import { RelatedArticles } from "@/components/blog/RelatedArticles";
import { useBlogPost, useBlogPosts } from "@/hooks/api/blogPosts";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import CalendlyModal from "@/components/CalendlyModal";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import { Analytics } from "@/lib/analytics";
import { useBlogScrollDepth } from "@/hooks/useBlogScrollDepth";
import SEOHead from "@/components/SEOHead";

const BLOG_CANONICAL_OVERRIDES: Record<string, string> = {
  "standard-telephonique-ia-pour-agence-immobiliere-le-guide-2026":
    "/standard-telephonique-immobilier",
  "comment-ne-plus-rater-un-seul-appel-acquereur-en-agence-immobiliere":
    "/blog/ne-manquez-plus-un-appel-acquereur-optimisez-votre-standard-telephonique",
};

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const { data: post, isLoading, isError } = useBlogPost(slug);
  const { data: allPosts } = useBlogPosts();

  // Calculate reading time (~200 words per minute)
  const readingTime = post
    ? Math.max(1, Math.ceil(post.content.split(/\s+/).length / 200))
    : 0;

  // Track blog scroll depth
  useBlogScrollDepth(slug);

  useEffect(() => {
    if (post) {
      Analytics.blogArticleViewed(slug!, post.title, readingTime);
    }
  }, [post, slug, readingTime]);

  // 404 handling
  if (!isLoading && (isError || !post)) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-[1120px] px-5 pb-20 pt-32 md:px-8">
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Article non trouvé
            </h1>
            <p className="text-muted-foreground mb-8 max-w-md">
              L'article que vous recherchez n'existe pas ou a été supprimé.
            </p>
            <Button onClick={() => navigate("/blog")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au blog
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {post && (() => {
        const rawTitle = post.meta_title || post.title;
        const suffix = " | Immovox";
        const maxTitle = 60;
        const title =
          rawTitle.length + suffix.length <= maxTitle
            ? `${rawTitle}${suffix}`
            : rawTitle.length <= maxTitle
              ? rawTitle
              : `${rawTitle.slice(0, maxTitle - 1).trimEnd()}…`;
        const articleLd = {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.meta_description || post.excerpt || undefined,
          image: post.cover_image_url || undefined,
          datePublished: post.published_at || undefined,
          dateModified: (post as any).updated_at || post.published_at || undefined,
          author: (post as any).author
            ? { "@type": "Person", name: (post as any).author }
            : { "@type": "Organization", name: "Immovox" },
          publisher: {
            "@type": "Organization",
            name: "Immovox",
            logo: { "@type": "ImageObject", url: "https://immovox.fr/logo.png" },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://immovox.fr/blog/${slug}`,
          },
        };
        return (
          <>
            <SEOHead
              title={title}
              description={post.meta_description || post.excerpt || ""}
              canonical={BLOG_CANONICAL_OVERRIDES[slug!] ?? `/blog/${slug}`}
              ogType="article"
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
            />
          </>
        );
      })()}
      <Header />
      
      <main className="container mx-auto px-4 py-16 pt-32">
        {/* Back button */}
        <Link to="/blog">
          <Button variant="ghost" size="sm" className="mb-8 -ml-3">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au blog
          </Button>
        </Link>

        {isLoading && (
          <div className="max-w-3xl mx-auto">
            <div className="h-10 w-2/3 bg-muted animate-pulse rounded mb-4" />
            <div className="h-6 w-1/3 bg-muted animate-pulse rounded mb-8" />
            <div className="h-64 w-full bg-muted animate-pulse rounded mb-8" />
            <div className="space-y-4">
              <div className="h-4 w-full bg-muted animate-pulse rounded" />
              <div className="h-4 w-5/6 bg-muted animate-pulse rounded" />
              <div className="h-4 w-4/6 bg-muted animate-pulse rounded" />
            </div>
          </div>
        )}

        {post && (
          <article className="max-w-[920px] mx-auto">
            {/* Article header */}
            <header className="mb-10 max-w-[820px]">
              <h1 className="text-4xl md:text-[56px] font-semibold tracking-[-.04em] text-foreground mb-6 leading-[1.05]">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                {post.published_at && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {format(new Date(post.published_at), "d MMMM yyyy", {
                        locale: fr,
                      })}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{readingTime} min de lecture</span>
                </div>
              </div>
            </header>

            {/* Cover image */}
            {post.cover_image_url && (
              <div className="mb-12 overflow-hidden">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            )}

            {/* Excerpt - Pull quote style */}
            {post.excerpt && (
              <div className="mb-12 border-l-2 border-accent py-2 pl-6">
                <p className="text-xl text-foreground/80 font-medium leading-8 italic">
                  {post.excerpt}
                </p>
              </div>
            )}

            {/* Content */}
            <BlogContent content={post.content} />

            {/* Interactive Demo Card CTA */}
            <div className="mt-16 border-y border-foreground/20 py-8 md:py-10">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-9 w-9 items-center justify-center border border-border">
                  <Headphones className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  Écoutez l'assistant Immovox en action
                </h3>
              </div>

              {/* Phone CTA - Primary */}
              <a
                href="tel:+33517948403"
                onClick={() => {
                  Analytics.blogCtaClicked(slug!, "appeler_demo");
                  Analytics.ctaClicked("appeler_demo", "blog_post_footer");
                }}
                className="group block border-y border-border py-5 transition-colors hover:border-primary/40 mb-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center bg-primary text-primary-foreground">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-foreground tracking-wide">
                        05 17 94 84 03
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Appel gratuit · Prix d'un appel local
                      </p>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-primary font-medium">
                    <span>Appeler maintenant</span>
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </a>

              {/* Description */}
              <p className="text-muted-foreground mb-8 max-w-lg">
                Découvrez en 30 secondes comment Immovox qualifie les appels 
                de vos prospects acquéreurs et locataires.
              </p>

              {/* Separator */}
              <div className="h-px bg-border mb-6" />

              {/* Secondary CTA */}
              <button
                onClick={() => {
                  Analytics.blogCtaClicked(slug!, "demander_demo");
                  Analytics.ctaClicked("demander_demo", "blog_post_footer");
                  setCalendlyOpen(true);
                }}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Calendar className="w-4 h-4" />
                <span>Vous préférez échanger avec l'équipe ?</span>
                <span className="font-medium text-primary underline underline-offset-2">
                  Planifier un appel
                </span>
              </button>
            </div>

            {/* Related Articles */}
            {allPosts && allPosts.length > 1 && (
              <RelatedArticles currentSlug={slug!} posts={allPosts} />
            )}
          </article>
        )}
      </main>

      <Footer />
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} source="blog_post_footer" />
      <ExitIntentPopup />
    </div>
  );
}
